import type { JSX, PointerEvent as RPointerEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { IconUndo, IconX } from './feedback-icons';
import { SHOT_JPEG_QUALITY, isShotRect, normRect, shotFormatBySize, shotLineWidth, type ShotRect } from './feedback-capture';

// Цвет рамки рисуется прямо в картинку (в canvas нельзя взять токен --fk-*), поэтому здесь литерал:
// красный и мягкая заливка — как в CRM, чтобы рамка читалась и на светлой, и на тёмной теме.
const FRAME_STROKE = '#E53935';
const FRAME_FILL = 'rgba(229,57,53,.12)';

function toBlob(c: HTMLCanvasElement, type: 'image/png' | 'image/jpeg', quality?: number): Promise<Blob | null> {
  return new Promise((res) => c.toBlob(res, type, quality));
}

/**
 * Снимок экрана с возможностью обвести область (красная рамка с мягкой заливкой,
 * как в CRM: frontend/src/components/feedback/feedback-ui.tsx → ScreenshotAnnotator).
 * Рисуем на canvas поверх картинки; `onChange` получает готовый PNG (или JPEG, если PNG
 * вышел больше лимита) с нарисованными рамками — при загрузке и после каждой правки.
 * `onRemove` — «Не прикладывать снимок».
 */
export function ScreenshotAnnotator({
  src,
  label,
  onChange,
  onRemove,
}: {
  /** data-URL снимка (captureScreen). */
  src: string;
  /** Подпись в углу превью (на саму картинку не попадает). */
  label?: string;
  onChange: (blob: Blob | null) => void;
  onRemove: () => void;
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [rects, setRects] = useState<ShotRect[]>([]);
  const [drawMode, setDrawMode] = useState(true);
  const start = useRef<{ x: number; y: number } | null>(null);
  // Номер последней выгрузки: toBlob асинхронный, устаревший результат не должен затереть свежий.
  const seq = useRef(0);
  // Свежие колбэки родителя без перезапуска загрузки картинки.
  const cb = useRef({ onChange, onRemove });
  useEffect(() => {
    cb.current = { onChange, onRemove };
  });

  const paint = useCallback((done: ShotRect[], dragging: ShotRect | null) => {
    const c = canvasRef.current;
    const img = imgRef.current;
    const ctx = c?.getContext('2d');
    if (!c || !img || !ctx) return;
    ctx.drawImage(img, 0, 0);
    ctx.lineWidth = shotLineWidth(img.naturalWidth);
    ctx.strokeStyle = FRAME_STROKE;
    ctx.fillStyle = FRAME_FILL;
    for (const r of dragging ? [...done, dragging] : done) {
      ctx.fillRect(r.x, r.y, r.w, r.h);
      ctx.strokeRect(r.x, r.y, r.w, r.h);
    }
  }, []);

  /** Перерисовать с готовыми рамками и отдать родителю PNG (JPEG, если PNG слишком тяжёлый). */
  const publish = useCallback(
    async (done: ShotRect[]): Promise<void> => {
      const c = canvasRef.current;
      if (!c) return;
      paint(done, null);
      const my = ++seq.current;
      let out = await toBlob(c, 'image/png');
      if (out && shotFormatBySize(out.size) === 'jpeg') out = await toBlob(c, 'image/jpeg', SHOT_JPEG_QUALITY);
      if (my === seq.current) cb.current.onChange(out);
    },
    [paint],
  );

  // Загрузка картинки и первичная отрисовка.
  useEffect(() => {
    let alive = true;
    const img = new Image();
    img.onload = () => {
      if (!alive) return;
      imgRef.current = img;
      const c = canvasRef.current;
      if (c) {
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
      }
      setRects([]);
      void publish([]);
    };
    img.onerror = () => {
      if (alive) cb.current.onRemove();
    };
    img.src = src;
    return () => {
      alive = false;
      seq.current += 1;
    };
  }, [src, publish]);

  // Точка касания в пикселях картинки (canvas на странице масштабируется по ширине).
  const pos = (e: RPointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const c = e.currentTarget;
    const b = c.getBoundingClientRect();
    if (!b.width || !b.height) return { x: 0, y: 0 };
    return { x: ((e.clientX - b.left) / b.width) * c.width, y: ((e.clientY - b.top) / b.height) * c.height };
  };

  return (
    <div className="fk-hub-fb-shot">
      <div className="fk-hub-fb-shot-frame">
        <canvas
          ref={canvasRef}
          className="fk-hub-fb-shot-canvas"
          data-draw={drawMode ? 'true' : 'false'}
          role="img"
          aria-label="Снимок экрана"
          onPointerDown={(e) => {
            if (!drawMode || e.button !== 0) return;
            e.currentTarget.setPointerCapture(e.pointerId);
            start.current = pos(e);
          }}
          onPointerMove={(e) => {
            if (start.current) paint(rects, normRect(start.current, pos(e)));
          }}
          onPointerUp={(e) => {
            const from = start.current;
            if (!from) return;
            start.current = null;
            const r = normRect(from, pos(e));
            if (!isShotRect(r)) {
              paint(rects, null);
              return;
            }
            const next = [...rects, r];
            setRects(next);
            void publish(next);
          }}
          onPointerCancel={() => {
            start.current = null;
            paint(rects, null);
          }}
        />
        {label ? <span className="fk-hub-fb-shot-label">{label}</span> : null}
      </div>
      <div className="fk-hub-fb-shot-bar">
        <button type="button" className="fk-hub-fb-tool" aria-pressed={drawMode} title="Зажмите и протяните мышью по снимку" onClick={() => setDrawMode(!drawMode)}>
          {drawMode ? 'Обводка включена' : 'Обвести область'}
        </button>
        {rects.length ? (
          <button
            type="button"
            className="fk-hub-fb-tool"
            onClick={() => {
              const next = rects.slice(0, -1);
              setRects(next);
              void publish(next);
            }}
          >
            <IconUndo size={13} />
            Отменить рамку
          </button>
        ) : null}
        <button type="button" className="fk-hub-fb-link" onClick={onRemove}>
          <IconX size={12} />
          Не прикладывать снимок
        </button>
      </div>
      <div className="fk-hub-fb-hint">Обведите место, где ошибка: зажмите и протяните по снимку.</div>
    </div>
  );
}
